import { DecimalPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProjectService } from '@shared/services/project.service';
import { Helper } from '@static/helpers';
import { IPCatType, IPCatVal, IProjectCodes } from '@static/models/project';

interface IProjectCodeCheckbox extends IPCatVal {
  checked: boolean;
  total_assignments: number;
}
interface IProjectTypeCheckbox extends IPCatType {
  complete: boolean;
  partial: boolean;
  total_assignments: number;
}
interface IProjectCodeFilterData {
  codes: FormControl;
  codesMode: FormControl;
}

@Component({
  selector: 'app-project-code',
  imports: [
    MatDialogModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatExpansionModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatButtonToggleModule,
    ReactiveFormsModule,
    DecimalPipe,
  ],
  templateUrl: './project-code.component.html',
  styleUrl: './project-code.component.scss',
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } }
  ]
})
export class ProjectCodeComponent {
  public dialogRef: MatDialogRef<ProjectCodeComponent> = inject(MatDialogRef<ProjectCodeComponent>);
  public data: IProjectCodeFilterData = inject(MAT_DIALOG_DATA);

  private _projectService: ProjectService = inject(ProjectService);

  protected projectCodeData: IProjectCodes;

  protected codeTypes: IProjectTypeCheckbox[];
  protected codeMap: Map<number, IProjectCodeCheckbox[]>;
  protected searchFilter: FormControl;
  private _activeCodes: number[];

  constructor() {
    this.projectCodeData = this._projectService.getProjectCodes();
    this.codeMap = new Map<number, IProjectCodeCheckbox[]>();
    this.codeTypes = [];
    this.searchFilter = new FormControl('');
    this._activeCodes = this.data.codes.value ?? [];

    this.loadCodes();
  }

  /**
   * Load the project codes into the required categories
   */
  protected loadCodes(): void {
    this.codeMap = new Map<number, IProjectCodeCheckbox[]>();
    this.codeTypes = this.projectCodeData.pCatTypes.map((type: IPCatType) => (
      {...type, complete: false, partial: false, total_assignments: 0}
    ));
    this.codeTypes.forEach((t: IProjectTypeCheckbox) => {
      let validCodes = this.projectCodeData.pCatVals
        .filter((c: IPCatVal) => c.proj_catg_type_id === t.proj_catg_type_id)
        .map((c: IPCatVal) => ({...c, checked: false, total_assignments: 0}));
      if (Helper.isDefined(this.searchFilter.value) && this.searchFilter.value !== '') {
        validCodes = validCodes.filter((c: IProjectCodeCheckbox) =>
          c.proj_catg_name?.toLowerCase().includes(this.searchFilter.value.toLowerCase()) ||
          c.proj_catg_short_name?.toLowerCase().includes(this.searchFilter.value.toLowerCase())
        );
      }
      validCodes.forEach((c: IProjectCodeCheckbox) => {
        if (this._activeCodes.includes(c.proj_catg_id)) c.checked = true;
        c.total_assignments = this.projectCodeData.projectCats.filter(p => p.proj_catg_id === c.proj_catg_id).length;
      });
      t.complete = validCodes.every(t => t.checked) ?? false;
      t.partial = (validCodes.some(t => t.checked) && !validCodes.every(t => t.checked)) ?? false;
      t.total_assignments = validCodes.reduce((sum, code) => sum + (code.total_assignments || 0), 0);
      this.codeMap.set(t.proj_catg_type_id, validCodes);
    });
  }

  /**
   * Update a parent type with a selection state
   *
   * @param type  type ref
   * @param state selected state
   */
  public updateType(type: IProjectTypeCheckbox, state: boolean, update = true): void {
    type.complete = state;
    type.partial = false;
    this.codeMap.get(type.proj_catg_type_id)?.forEach((code) => {
      code.checked = state;
      if (state) this._activeCodes.push(code.proj_catg_id);
      else this._activeCodes = this._activeCodes.filter((sCode) => sCode !== code.proj_catg_id);
    });
    if (update) {
      this.data.codes.setValue(this._activeCodes);
    }
  }

  /**
   * Update a project code selection state
   *
   * @param code  code ref
   * @param state selected state
   */
  public updateCode(code: IProjectCodeCheckbox, state: boolean): void {
    code.checked = state;
    if (state) this._activeCodes.push(code.proj_catg_id);
    else this._activeCodes = this._activeCodes.filter((sCode) => +sCode !== code.proj_catg_id);
    const parentType = this.codeTypes.find((type) => type.proj_catg_type_id === code.proj_catg_type_id);
    if (parentType !== undefined) {
      const parentCodes = this.codeMap.get(parentType.proj_catg_type_id);
      parentType.partial = (parentCodes?.some(t => t.checked) && !parentCodes?.every(t => t.checked)) ?? false;
      parentType.complete = parentCodes?.every(t => t.checked) ?? false;
    }
    this.data.codes.setValue(this._activeCodes);
  }

  /**
   * Clear all active selections
   */
  public clearAll(): void {
    this.codeTypes.forEach((type) => this.updateType(type, false, false));
    this.data.codes.setValue(this._activeCodes);
  }

  /**
   * Close the dialog
   */
  public close(): void {
    this.dialogRef.close();
  }
}
